/**
 * FACTORY WORLD STORE
 *
 * Zustand store for managing the Factory World state.
 * This is the central state for projects, agents, and zones.
 *
 * Uses DataSource abstraction: cloud workspaces use API, local workspaces use SQLite.
 */

import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { githubApi } from "@/lib/github-api";
import { useWorkspaceStore, getWorkspaceType, isLocalWorkspace } from "@/stores/workspaceStore";
import { useGitStore, getAuthParams } from "@/stores/gitStore";
import { getDataSource } from "@/lib/data";
import type { DataSource } from "@/lib/data/types";
import { mapTaskFromApi } from "@/lib/data/mappers";
import { getMergeErrorMessage } from "@/lib/mergeErrors";
import { normalizeWorkflowIssueType } from "@/lib/workflow/startFields";

// Debounce timers for save operations. We keep the latest pending saveFn per key
// so it can be flushed immediately (e.g. on app close) instead of being silently
// dropped if the app quits inside the debounce window.
const saveTimers: Record<string, NodeJS.Timeout> = {};
const pendingSaveFns: Record<string, () => Promise<void>> = {};
const SAVE_DEBOUNCE_MS = 500;

// Pipeline polling
let pipelinePollTimer: NodeJS.Timeout | null = null;
const PIPELINE_POLL_MS = 5000;
export const PIPELINE_POLL_IDLE_MS = 30000;

export function pickPollInterval(hasActivePipelines: boolean): number {
  return hasActivePipelines ? PIPELINE_POLL_MS : PIPELINE_POLL_IDLE_MS;
}

let isFetchingWorld = false;

// AbortController for in-flight fetches (prevents state bleed on workspace switch)
let worldFetchAbort: AbortController | null = null;

function debouncedSave(key: string, saveFn: () => Promise<void>) {
  if (saveTimers[key]) {
    clearTimeout(saveTimers[key]);
  }
  pendingSaveFns[key] = saveFn;
  saveTimers[key] = setTimeout(async () => {
    delete saveTimers[key];
    delete pendingSaveFns[key];
    await saveFn();
  }, SAVE_DEBOUNCE_MS);
}

/**
 * Run all pending debounced saves immediately. Call before the app closes so a
 * change made within the last {@link SAVE_DEBOUNCE_MS} isn't lost. Errors are left
 * on the store's `saveError` (set inside each saveFn) rather than thrown.
 */
export async function flushPendingWorldSaves(): Promise<void> {
  const keys = Object.keys(pendingSaveFns);
  await Promise.all(
    keys.map(async (key) => {
      const fn = pendingSaveFns[key];
      if (saveTimers[key]) {
        clearTimeout(saveTimers[key]);
        delete saveTimers[key];
      }
      delete pendingSaveFns[key];
      try {
        await fn();
      } catch {
        // saveError is already set inside the saveFn; swallow to flush the rest.
      }
    }),
  );
}

/**
 * Get the DataSource for the currently active workspace.
 */
async function getActiveDataSource(): Promise<DataSource> {
  const workspaceState = useWorkspaceStore.getState();
  const wsType = workspaceState.getActiveWorkspaceType();

  if (wsType === "local") {
    const activeWorkspaceId = workspaceState.activeWorkspaceId;
    if (!isLocalWorkspace(activeWorkspaceId)) {
      throw new Error("No active local workspace selected");
    }

    // Keep local workspace context in sync before any SQLite operation.
    const { setActiveLocalWorkspace } = await import("@/lib/data/local-source");
    setActiveLocalWorkspace(activeWorkspaceId);
  }

  return getDataSource(wsType);
}

/**
 * Guard: throw if active workspace is local — for cloud-only operations.
 */
function assertCloudWorkspace(operation: string): void {
  const wsType = useWorkspaceStore.getState().getActiveWorkspaceType();
  if (wsType === "local") {
    throw new Error(`${operation} requires a cloud workspace`);
  }
}

/**
 * Config flag to control whether GitHub issues should be closed when moved to Done.
 * Default: false - issues remain open when moved to Done stage.
 * Set to true to automatically close issues when they reach the Done stage.
 */
export const CLOSE_ISSUE_ON_DONE = false;

export interface PlanLimitError {
  code: "PLAN_LIMIT_PROJECTS" | "PLAN_LIMIT_DAILY_RUNS" | "PLAN_LIMIT_CREDITS";
  limit: number;
  current: number;
  planName: string;
  upgradeTo: string;
}

export type ProjectStatus = "healthy" | "degraded" | "failing";
export type AgentState = "idle" | "busy" | "queued";
export type AgentAttentionState = "none" | "completed" | "stuck" | "human_needed" | "error";
export type PipelineStage = "backlog" | "planning" | "in_progress" | "ai_review" | "human_review" | "done" | "error";
export type ProjectShape = "square" | "circle" | "rectangle" | "hexagon" | "gear" | "database" | "server" | "factory";
export type PipelineDirection = "forward" | "backward" | "bidirectional";
export type PipelineStyle = "solid" | "dashed" | "dotted";
export type PipelineTraffic = "normal" | "heavy" | "light" | "none";

export type Priority = "no-priority" | "low" | "medium" | "high" | "urgent";
export type Complexity = "no-complexity" | "low" | "medium" | "high" | "extreme";
export type Impact = "no-impact" | "low" | "medium" | "high" | "critical";

const BACKEND_TO_FRONTEND_STAGE: Record<string, PipelineStage> = {
  // Backend Python enum values → frontend
  spec: "backlog",
  plan: "planning",
  code: "in_progress",
  qa: "ai_review",
  pr: "human_review",
  // Webhook/legacy values
  todo: "backlog",
  pending: "backlog",
  completed: "done",
  // Frontend values passthrough
  backlog: "backlog",
  planning: "planning",
  in_progress: "in_progress",
  ai_review: "ai_review",
  human_review: "human_review",
  done: "done",
  error: "error",
};

export function normalizePipelineStage(stage: string | undefined | null): PipelineStage {
  if (!stage) return "backlog";
  return BACKEND_TO_FRONTEND_STAGE[stage] ?? "backlog";
}

export interface Zone {
  id: number;
  name: string;
  color: string;
  startX: number;
  startY: number;
  width: number;
  height: number;
  isEditable?: boolean;
  workspaceId?: number;
}

export interface ProjectConfig {
  devCommand?: string;
  port?: number;
  dockerfilePath?: string;
  buildContext?: string;
  claudeMdPath?: string;
  domain?: string;
  blogUrl?: string;
  [key: string]: unknown;
}

export interface Project {
  id: number;
  name: string;
  status: ProjectStatus;
  x: number;
  y: number;
  width?: number;
  height?: number;
  zoneId?: number;
  description?: string;
  lastDeployed?: string;
  githubRepo?: string;
  fargateCluster?: string;
  containerCount?: number;
  shape?: ProjectShape;
  icon?: string;
  customShapeImage?: string;
  localPath?: string;
  workspaceId?: number;
  subdirectory?: string;
  monorepoGroup?: string;
  projectConfig?: ProjectConfig;
  project_category: 'coding' | 'marketing';
  project_subtype: string | null;
}

export interface Pipeline {
  id: string;
  fromId: number;
  toId: number;
  fromType: "project" | "zone";
  toType: "project" | "zone";
  direction: PipelineDirection;
  style?: PipelineStyle;
  traffic?: PipelineTraffic;
  color?: string;
  label?: string;
  animated?: boolean;
}

export type AgentProfile = "auto" | "fast" | "balanced" | "powerful";

export interface AgentConfig {
  profile: AgentProfile;
  humanReviewRequired: boolean;
  autoCommit: boolean;
  autoPush: boolean;
  maxTokens?: number;
  model?: string;
}

export interface Agent {
  id: string;
  projectId: number;
  state: AgentState;
  name?: string;
  taskDescription?: string;
  taskId?: string;
  pipelineStage?: PipelineStage;
  config?: AgentConfig;
  currentPrompt?: string;
  startedAt?: string;
  estimatedCompletion?: string;
  attentionState?: AgentAttentionState;
  attentionMessage?: string;
  attentionDetails?: string;
}

export type WorkflowStatus = "queued" | "running" | "completed" | "failed" | "cancelled" | "paused";

export interface Task {
  id: string;
  projectId: number;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
  githubIssueUrl?: string;
  pipelineStage: PipelineStage;
  status: "pending" | "in_progress" | "review" | "completed";
  assignedAgentId?: string;
  issueType: "wp" | "bug" | "feature" | "hotfix" | "refactor";
  workflowId?: string;
  workflowRunId?: string;
  workflowStatus?: WorkflowStatus;
  humanReviewRequired?: boolean;
  baseBranch?: string;
  priority?: Priority;
  complexity?: Complexity;
  impact?: Impact;
}

interface WorldState {
  zones: Zone[];
  projects: Project[];
  agents: Agent[];
  tasks: Task[];
  pipelines: Pipeline[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  saveError: string | null;
  syncingTaskId: string | null;
  planLimitError: PlanLimitError | null;

  fetchWorldData: () => Promise<void>;
  addProject: (project: Omit<Project, "id">, workspaceId: number | string) => Promise<Project | null>;
  updateProject: (id: number, updates: Partial<Project>, options?: { immediate?: boolean }) => Promise<boolean>;
  removeProject: (id: number) => Promise<boolean>;
  updateAgentState: (agentId: string, state: AgentState, taskId?: string) => void;
  updateAgentAttention: (agentId: string, attentionState: AgentAttentionState, message?: string, details?: string) => void;
  removeAgent: (agentId: string) => void;
  addTask: (task: Omit<Task, "id">, projectId: number) => Promise<Task | null>;
  updateTask: (taskId: string, updates: Partial<Task>) => Promise<boolean>;
  moveTaskToStage: (taskId: string, stage: PipelineStage) => Promise<boolean>;
  updateTaskWithGitHubSync: (taskId: string, stage: PipelineStage, labels: string[], issueState?: "open" | "closed") => Promise<void>;
  syncProjectIssues: (projectId: number) => Promise<{ synced: number; created: number; updated: number } | null>;
  retryTaskPipeline: (taskId: string) => Promise<boolean>;
  resumeTaskFromFailedNode: (taskId: string, action?: string, targetNodeId?: string, feedback?: string) => Promise<boolean>;
  startTask: (taskId: string) => Promise<boolean>;
  stopTask: (taskId: string) => Promise<boolean>;
  mergeTaskPR: (taskId: string) => Promise<boolean>;
  fixTaskMergeWithAI: (taskId: string) => Promise<boolean>;
  refreshTask: (taskId: string) => Promise<void>;
  addZone: (zone: Omit<Zone, "id">, workspaceId: number | string) => Promise<Zone | null>;
  updateZone: (id: number, updates: Partial<Zone>, options?: { immediate?: boolean }) => Promise<boolean>;
  removeZone: (id: number) => Promise<boolean>;
  addPipeline: (pipeline: Omit<Pipeline, "id">) => Promise<Pipeline | null>;
  updatePipeline: (id: string, updates: Partial<Pipeline>) => Promise<boolean>;
  removePipeline: (id: string) => Promise<boolean>;
  removeTask: (taskId: string) => Promise<boolean>;
  clearWorldState: () => void;
  clearSaveError: () => void;
  clearPlanLimitError: () => void;
}

/**
 * Parse a plan limit error from an API error message or local-source Error object.
 * Backend returns 403 with JSON detail like: {"code":"PLAN_LIMIT_PROJECTS","limit":3,...}
 * Local-source throws Error with .code, .limit, .current properties directly.
 */
export function parsePlanLimitError(error: unknown): PlanLimitError | null {
  const LIMIT_CODES = ["PLAN_LIMIT_PROJECTS", "PLAN_LIMIT_DAILY_RUNS", "PLAN_LIMIT_CREDITS"];

  // Path 1: local-source errors — Error object with .code property
  if (error && typeof error === "object" && "code" in error) {
    const errObj = error as Record<string, unknown>;
    if (typeof errObj.code === "string" && LIMIT_CODES.includes(errObj.code)) {
      const limitNum = Number(errObj.limit);
      const currentNum = Number(errObj.current);
      return {
        code: errObj.code as PlanLimitError["code"],
        limit: Number.isFinite(limitNum) ? limitNum : 0,
        current: Number.isFinite(currentNum) ? currentNum : 0,
        planName: (errObj.planName ?? errObj.plan_name ?? "free") as string,
        upgradeTo: (errObj.upgradeTo ?? errObj.upgrade_to ?? "pro") as string,
      };
    }
  }

  // Path 2: API errors — "403: {json}" from api.ts
  const msg = error instanceof Error ? error.message : String(error);
  if (!msg.includes("403")) return null;
  try {
    const jsonStart = msg.indexOf("{");
    if (jsonStart === -1) return null;
    const parsed = JSON.parse(msg.slice(jsonStart));
    // FastAPI HTTPException payloads are usually wrapped in { detail: {...} }.
    const payload = parsed?.detail && typeof parsed.detail === "object" ? parsed.detail : parsed;
    const code = payload?.code;
    if (!LIMIT_CODES.includes(code)) return null;
    const limitNum = Number(payload?.limit);
    const currentNum = Number(payload?.current);
    return {
      code,
      limit: Number.isFinite(limitNum) ? limitNum : 0,
      current: Number.isFinite(currentNum) ? currentNum : 0,
      planName: payload?.plan_name ?? "free",
      upgradeTo: payload?.upgrade_to ?? "pro",
    };
  } catch {
    // Not a plan limit error
  }
  return null;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }
  try {
    const serialized = JSON.stringify(error);
    if (serialized && serialized !== "{}") return serialized;
  } catch {
    // Fall through to generic message.
  }
  return "Unknown error";
}

export const useWorldStore = create<WorldState>()(
  subscribeWithSelector((set, get) => ({
    zones: [],
    projects: [],
    agents: [],
    tasks: [],
    pipelines: [],
    isLoading: false,
    isSaving: false,
    error: null,
    saveError: null,
    syncingTaskId: null,
    planLimitError: null,

    clearPlanLimitError: () => set({ planLimitError: null }),

    clearWorldState: () => {
      // Abort any in-flight fetches
      if (worldFetchAbort) {
        worldFetchAbort.abort();
        worldFetchAbort = null;
      }
      // Stop pipeline polling
      if (pipelinePollTimer) {
        clearTimeout(pipelinePollTimer);
        pipelinePollTimer = null;
      }
      // Cancel pending debounced saves
      for (const key of Object.keys(saveTimers)) {
        clearTimeout(saveTimers[key]);
        delete saveTimers[key];
      }
      isFetchingWorld = false;
      set({
        zones: [],
        projects: [],
        agents: [],
        tasks: [],
        pipelines: [],
        isLoading: false,
        error: null,
      });
    },

    fetchWorldData: async () => {
      if (isFetchingWorld) return;
      isFetchingWorld = true;

      // Abort previous in-flight fetch
      if (worldFetchAbort) worldFetchAbort.abort();
      worldFetchAbort = new AbortController();
      const currentAbort = worldFetchAbort;

      try {
        const isInitialLoad = get().projects.length === 0;
        set({ isLoading: isInitialLoad, error: null });

        const ds = await getActiveDataSource();
        const data = await ds.getWorldData();

        // Check if this fetch was aborted (workspace switched while loading)
        if (currentAbort.signal.aborted) return;

        // Filter by active workspace (cloud mode returns all workspaces)
        const activeBackendId = useWorkspaceStore.getState().getActiveWorkspaceBackendId();
        let filteredProjects = data.projects ?? [];
        let filteredZones = data.zones ?? [];
        let filteredAgents = data.agents ?? [];
        let filteredTasks = data.tasks ?? [];

        if (activeBackendId != null) {
          filteredProjects = filteredProjects.filter((p) => p.workspaceId === activeBackendId);
          filteredZones = filteredZones.filter((z) => z.workspaceId === activeBackendId);
          const projectIds = new Set(filteredProjects.map((p) => p.id));
          filteredTasks = filteredTasks.filter((t) => projectIds.has(t.projectId));
          filteredAgents = filteredAgents.filter((a) => projectIds.has(a.projectId));
        }

        const mappedTasks = filteredTasks;
        set({
          zones: filteredZones,
          projects: filteredProjects,
          agents: filteredAgents,
          tasks: mappedTasks,
          pipelines: data.pipelines ?? [],
          isLoading: false,
        });

        // Reschedule pipeline polling based on active pipelines (cloud only).
        // A self-rescheduling timeout lets the interval adapt per tick: fast
        // while pipelines run, backing off to idle cadence when nothing is
        // active. Each fetch re-arms the next tick, so we always clear first.
        const wsType = useWorkspaceStore.getState().getActiveWorkspaceType();
        if (pipelinePollTimer) {
          clearTimeout(pipelinePollTimer);
          pipelinePollTimer = null;
        }
        if (wsType === "cloud") {
          const hasActivePipelines = mappedTasks.some(
            (t) => t.workflowStatus === "running" || t.workflowStatus === "queued"
          );
          pipelinePollTimer = setTimeout(() => {
            useWorldStore.getState().fetchWorldData();
          }, pickPollInterval(hasActivePipelines));
        }
        // No polling for local workspaces (timer already cleared above).
      } catch (error) {
        if (currentAbort.signal.aborted) return;
        set({ error: (error as Error).message, isLoading: false });
      } finally {
        isFetchingWorld = false;
      }
    },

    addProject: async (project, workspaceId) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        const mapped = await ds.projects.create({
          workspaceId,
          name: project.name,
          zoneId: project.zoneId,
          x: project.x,
          y: project.y,
          description: project.description,
          githubRepo: project.githubRepo,
          status: project.status || "healthy",
          shape: project.shape,
          localPath: project.localPath,
          subdirectory: project.subdirectory,
          monorepoGroup: project.monorepoGroup,
          projectConfig: project.projectConfig,
          project_category: project.project_category,
          project_subtype: project.project_subtype,
        });
        set((state) => ({
          projects: [...state.projects, mapped],
          isSaving: false,
        }));
        return mapped;
      } catch (error) {
        const planError = parsePlanLimitError(error);
        if (planError) {
          set({ planLimitError: planError, isSaving: false });
        } else {
          set({ saveError: (error as Error).message, isSaving: false });
        }
        return null;
      }
    },

    updateProject: async (id, updates, options = {}) => {
      // Optimistic UI update - happens immediately
      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === id ? { ...p, ...updates } : p
        ),
      }));

      let success = true;

      const saveToBackend = async () => {
        set({ isSaving: true, saveError: null });
        try {
          const currentProject = get().projects.find(p => p.id === id);
          if (!currentProject) return;

          const ds = await getActiveDataSource();
          await ds.projects.update(id, {
            x: currentProject.x,
            y: currentProject.y,
            name: currentProject.name,
            description: currentProject.description,
            status: currentProject.status,
            zoneId: currentProject.zoneId,
            shape: currentProject.shape,
            githubRepo: currentProject.githubRepo,
            localPath: currentProject.localPath,
            subdirectory: currentProject.subdirectory,
            monorepoGroup: currentProject.monorepoGroup,
            projectConfig: currentProject.projectConfig,
          });
          set({ isSaving: false });
        } catch (error) {
          set({ saveError: (error as Error).message, isSaving: false });
          success = false;
        }
      };

      if (options?.immediate) {
        await saveToBackend();
        return success;
      } else {
        debouncedSave(`project-${id}`, saveToBackend);
      }
      return true;
    },

    removeProject: async (id) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        await ds.projects.delete(id);
        set((state) => ({
          projects: state.projects.filter((p) => p.id !== id),
          agents: state.agents.filter((a) => a.projectId !== id),
          tasks: state.tasks.filter((t) => t.projectId !== id),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        console.error("Failed to delete project:", error);
        set({ saveError: (error as Error).message, isSaving: false });
        return false;
      }
    },

    updateAgentState: (agentId, state, taskId) => {
      set((s) => ({
        agents: s.agents.map((a) =>
          a.id === agentId ? { ...a, state, taskId } : a
        ),
      }));
    },

    updateAgentAttention: (agentId, attentionState, message, details) => {
      set((s) => ({
        agents: s.agents.map((a) =>
          a.id === agentId
            ? { ...a, attentionState, attentionMessage: message, attentionDetails: details }
            : a
        ),
      }));
    },

    removeAgent: (agentId) => {
      set((state) => ({
        agents: state.agents.filter((a) => a.id !== agentId),
      }));
    },

    addTask: async (task, projectId) => {
      set({ isSaving: true, saveError: null });
      try {
        let githubIssueUrl: string | undefined = task.githubIssueUrl;

        const normalizedIssueType = (task.issueType || "feature") as Task["issueType"];
        const normalizedWorkflowType = normalizeWorkflowIssueType(normalizedIssueType);
        const typeLabel =
          normalizedWorkflowType === "bug"
            ? "factory:type/bug"
            : normalizedWorkflowType === "refactor"
            ? "factory:type/refactor"
            : "factory:type/feature";

        const stageLabelMap: Record<PipelineStage, string> = {
          backlog: "factory:stage/backlog",
          planning: "factory:stage/planning",
          in_progress: "factory:stage/in-progress",
          ai_review: "factory:stage/review",
          human_review: "factory:stage/review",
          done: "factory:stage/done",
          error: "factory:stage/error",
        };

        const statusLabel = task.status === "completed"
          ? "factory:status/done"
          : task.status === "in_progress"
          ? "factory:status/in-progress"
          : task.status === "review"
          ? "factory:status/review"
          : "factory:status/pending";

        const stageLabel = stageLabelMap[task.pipelineStage] ?? "factory:stage/backlog";

        const buildIssueBody = (): string => {
          const meta = task.metadata || {};
          const parts: string[] = [];
          if (task.description) parts.push(task.description);

          const startInput = meta.startInput || meta.start_input;
          if (startInput && typeof startInput === "object" && !Array.isArray(startInput)) {
            const coveredKeys =
              normalizedWorkflowType === "bug"
                ? new Set(["stepsToReproduce", "steps", "expectedBehavior", "expected", "actualBehavior", "actual", "severity"])
                : normalizedWorkflowType === "refactor"
                ? new Set(["motivation", "targetFiles", "approach", "riskLevel"])
                : new Set(["userStory", "acceptanceCriteria", "scope", "constraints"]);
            const entries = Object.entries(startInput as Record<string, any>).filter(
              ([key]) => key !== "title" && key !== "description" && !coveredKeys.has(key),
            );
            if (entries.length > 0) {
              parts.push("## Intake Fields");
              for (const [key, value] of entries) {
                const label = key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/_/g, " ")
                  .replace(/^./, (letter: string) => letter.toUpperCase())
                  .trim();
                if (Array.isArray(value)) {
                  parts.push(`### ${label}`);
                  parts.push(value.map((item) => `- ${item}`).join("\n"));
                } else {
                  parts.push(`**${label}:** ${String(value)}`);
                }
              }
            }
          }

          if (normalizedWorkflowType === "bug") {
            const steps: string[] = meta.stepsToReproduce || meta.steps || [];
            if (steps.length) {
              parts.push("## Steps to Reproduce");
              parts.push(steps.map((s: string) => `- ${s}`).join("\n"));
            }
            if (meta.expectedBehavior) {
              parts.push("## Expected");
              parts.push(meta.expectedBehavior);
            }
            if (meta.actualBehavior) {
              parts.push("## Actual");
              parts.push(meta.actualBehavior);
            }
            if (meta.severity) {
              parts.push(`**Severity:** ${meta.severity}`);
            }
          } else if (normalizedWorkflowType === "refactor") {
            if (meta.motivation) {
              parts.push("## Motivation");
              parts.push(meta.motivation);
            }
            if (meta.targetFiles?.length) {
              parts.push("## Target Files");
              parts.push(meta.targetFiles.map((f: string) => `- ${f}`).join("\n"));
            }
            if (meta.approach) {
              parts.push("## Proposed Approach");
              parts.push(meta.approach);
            }
            if (meta.riskLevel) {
              parts.push(`**Risk Level:** ${meta.riskLevel}`);
            }
          } else {
            if (meta.userStory) {
              parts.push("## User Story");
              parts.push(meta.userStory);
            }
            if (meta.acceptanceCriteria?.length) {
              parts.push("## Acceptance Criteria");
              parts.push(meta.acceptanceCriteria.map((a: string) => `- [ ] ${a}`).join("\n"));
            }
            if (meta.scope) {
              parts.push(`**Scope:** ${meta.scope}`);
            }
            if (meta.constraints) {
              parts.push("## Constraints");
              parts.push(meta.constraints);
            }
          }

          return parts.filter(Boolean).join("\n\n").trim();
        };

        // If project has a GitHub repo and no issue URL yet, create a GitHub issue first
        const project = get().projects.find((p) => p.id === projectId);
        if (project?.githubRepo && !githubIssueUrl) {
          const auth = getAuthParams(useGitStore.getState());
          if (auth) {
            try {
              const [owner, repo] = project.githubRepo.split("/");
              if (owner && repo) {
                const issue = await githubApi.createIssue(owner, repo, {
                  title: task.title,
                  body: buildIssueBody(),
                  labels: [typeLabel, stageLabel, statusLabel],
                }, auth);
                githubIssueUrl = issue.url || issue.html_url;
              }
            } catch (e) {
              console.warn("GitHub issue creation failed, creating local-only task:", e);
            }
          }
        }

        const ds = await getActiveDataSource();
        const normalizedResponse = await ds.tasks.create({
          projectId,
          title: task.title,
          description: task.description,
          metadata: task.metadata,
          pipelineStage: task.pipelineStage,
          status: task.status || "pending",
          issueType: task.issueType,
          githubIssueUrl,
          workflowId: task.workflowId,
        });

        set((state) => ({
          tasks: [...state.tasks, normalizedResponse],
          isSaving: false,
        }));
        return normalizedResponse;
      } catch (error) {
        const msg = (error as Error).message ?? String(error);
        console.error("[addTask] failed:", msg, error);
        set({ saveError: msg, isSaving: false });
        throw error;
      }
    },

    updateTask: async (taskId, updates) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        const updatePayload = {
          title: updates.title,
          description: updates.description,
          pipelineStage: updates.pipelineStage,
          status: updates.status,
          issueType: updates.issueType,
          workflowId: updates.workflowId,
          assignedAgentId: updates.assignedAgentId,
          humanReviewRequired: updates.humanReviewRequired,
          baseBranch: updates.baseBranch,
          priority: updates.priority,
          complexity: updates.complexity,
          impact: updates.impact,
          metadata: updates.metadata,
        };
        const normalizedUpdatedTask = await ds.tasks.update(taskId, updatePayload);
        if (updates.workflowId && normalizedUpdatedTask.workflowId !== updates.workflowId) {
          console.warn("[updateTask] workflowId mismatch!", {
            sent: updates.workflowId,
            received: normalizedUpdatedTask.workflowId,
            taskId,
          });
        }
        // Abort any in-flight world fetch to prevent stale poll data from overwriting this update
        if (worldFetchAbort) {
          worldFetchAbort.abort();
          worldFetchAbort = null;
        }
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  ...normalizedUpdatedTask,
                  ...updates,
                  pipelineStage: normalizedUpdatedTask.pipelineStage ?? t.pipelineStage,
                  workflowId: normalizedUpdatedTask.workflowId ?? updates.workflowId ?? t.workflowId,
                }
              : t
          ),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        set({ saveError: (error as Error).message, isSaving: false });
        return false;
      }
    },

    moveTaskToStage: async (taskId, stage) => {
      return get().updateTask(taskId, { pipelineStage: stage });
    },

    updateTaskWithGitHubSync: async (taskId, stage, labels, issueState) => {
      const task = get().tasks.find((t) => t.id === taskId);
      if (!task) {
        throw new Error("Task not found");
      }

      const previousStage = task.pipelineStage;

      set({ syncingTaskId: taskId });

      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.id === taskId ? { ...t, pipelineStage: stage } : t
        ),
      }));

      if (!task.githubIssueUrl) {
        set({ syncingTaskId: null });
        return;
      }

      try {
        const urlMatch = task.githubIssueUrl.match(
          /github\.com\/([^/]+)\/([^/]+)\/issues\/(\d+)/
        );
        if (!urlMatch) {
          throw new Error("Invalid GitHub issue URL");
        }

        const [, owner, repo, issueNumber] = urlMatch;

        const auth = getAuthParams(useGitStore.getState());
        if (!auth) {
          throw new Error("No GitHub auth configured");
        }

        await githubApi.updateIssueLabels(
          owner, repo, parseInt(issueNumber), labels, auth, issueState
        );

        // Also update backend DB so pipeline triggers can fire
        try {
          const ds = await getActiveDataSource();
          const normalized = await ds.tasks.update(taskId, { pipelineStage: stage });
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === taskId ? { ...t, ...normalized } : t
            ),
          }));
        } catch (patchErr) {
          console.warn("Failed to patch backend stage:", patchErr);
        }

        set({ syncingTaskId: null });
      } catch (error) {
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId ? { ...t, pipelineStage: previousStage } : t
          ),
          syncingTaskId: null,
        }));
        throw error;
      }
    },

    retryTaskPipeline: async (taskId) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        const normalizedTask = await ds.tasks.retry(taskId);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId ? { ...t, ...normalizedTask } : t
          ),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        set({ saveError: (error as Error).message, isSaving: false });
        return false;
      }
    },

    resumeTaskFromFailedNode: async (taskId, action = "approve", targetNodeId?, feedback?) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        const normalizedTask = await ds.tasks.resume(taskId, action, targetNodeId, feedback);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId ? { ...t, ...normalizedTask } : t
          ),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        set({ saveError: (error as Error).message, isSaving: false });
        return false;
      }
    },

    startTask: async (taskId) => {
      const task = get().tasks.find((t) => t.id === taskId);
      if (!task) return false;

      const previousStage = task.pipelineStage;
      const previousStatus = task.workflowStatus;

      // Optimistic update
      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.id === taskId
            ? { ...t, pipelineStage: "in_progress" as PipelineStage, workflowStatus: "queued" as WorkflowStatus }
            : t
        ),
        isSaving: true,
        saveError: null,
      }));

      try {
        const ds = await getActiveDataSource();
        const normalizedTask = await ds.tasks.start(taskId);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId ? { ...t, ...normalizedTask } : t
          ),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        // Rollback
        const planError = parsePlanLimitError(error);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId
              ? { ...t, pipelineStage: previousStage, workflowStatus: previousStatus }
              : t
          ),
          ...(planError
            ? { planLimitError: planError }
            : { saveError: toErrorMessage(error) }),
          isSaving: false,
        }));
        return false;
      }
    },

    stopTask: async (taskId) => {
      const task = get().tasks.find((t) => t.id === taskId);
      if (!task) return false;

      const previousStage = task.pipelineStage;
      const previousStatus = task.workflowStatus;

      // Optimistic update
      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.id === taskId
            ? { ...t, pipelineStage: "backlog" as PipelineStage, workflowStatus: undefined, metadata: { ...t.metadata, workflow_steps: undefined, bugfix_steps: undefined, current_step: undefined } }
            : t
        ),
        isSaving: true,
        saveError: null,
      }));

      try {
        const ds = await getActiveDataSource();
        const normalizedTask = await ds.tasks.stop(taskId);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId ? { ...t, ...normalizedTask } : t
          ),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        // Rollback
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId
              ? { ...t, pipelineStage: previousStage, workflowStatus: previousStatus }
              : t
          ),
          saveError: toErrorMessage(error),
          isSaving: false,
        }));
        return false;
      }
    },

    mergeTaskPR: async (taskId) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        const normalizedTask = await ds.tasks.merge(taskId);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId ? { ...t, ...normalizedTask } : t
          ),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        const mergeMessage = getMergeErrorMessage(toErrorMessage(error));
        set({ saveError: mergeMessage, isSaving: false });
        throw new Error(mergeMessage);
      }
    },

    fixTaskMergeWithAI: async (taskId) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        const normalizedTask = await ds.tasks.mergeWithAI(taskId);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId ? { ...t, ...normalizedTask } : t
          ),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        const mergeMessage = getMergeErrorMessage(toErrorMessage(error));
        set({ saveError: mergeMessage, isSaving: false });
        throw new Error(mergeMessage);
      }
    },

    refreshTask: async (taskId) => {
      try {
        const ds = await getActiveDataSource();
        const normalizedTask = await ds.tasks.getById(taskId);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId ? { ...t, ...normalizedTask } : t
          ),
        }));
      } catch {
        // silently fail — not critical
      }
    },

    syncProjectIssues: async (projectId) => {
      assertCloudWorkspace("Syncing project issues");
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        const result = await ds.syncProjectIssues(projectId);

        // Refresh tasks for this project
        const projectTasks = await ds.tasks.list(projectId);
        set((state) => ({
          tasks: [
            ...state.tasks.filter((t) => t.projectId !== projectId),
            ...projectTasks,
          ],
          isSaving: false,
        }));
        return result;
      } catch (error) {
        set({ saveError: (error as Error).message, isSaving: false });
        throw error;
      }
    },

    addZone: async (zone, workspaceId) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        const mapped = await ds.zones.create({
          workspaceId,
          name: zone.name,
          color: zone.color,
          startX: zone.startX,
          startY: zone.startY,
          width: zone.width,
          height: zone.height,
        });
        set((state) => ({
          zones: [...state.zones, mapped],
          isSaving: false,
        }));
        return mapped;
      } catch (error) {
        set({ saveError: (error as Error).message, isSaving: false });
        return null;
      }
    },

    updateZone: async (id, updates, options = {}) => {
      // Optimistic UI update - happens immediately
      set((state) => ({
        zones: state.zones.map((z) =>
          z.id === id ? { ...z, ...updates } : z
        ),
      }));

      const saveToBackend = async () => {
        set({ isSaving: true, saveError: null });
        try {
          const currentZone = get().zones.find(z => z.id === id);
          if (!currentZone) return;

          const ds = await getActiveDataSource();
          await ds.zones.update(id, {
            name: currentZone.name,
            color: currentZone.color,
            startX: currentZone.startX,
            startY: currentZone.startY,
            width: currentZone.width,
            height: currentZone.height,
          });
          set({ isSaving: false });
        } catch (error) {
          console.error("Failed to update zone:", error);
          set({ saveError: (error as Error).message, isSaving: false });
        }
      };

      if (options?.immediate) {
        await saveToBackend();
      } else {
        debouncedSave(`zone-${id}`, saveToBackend);
      }
      return true;
    },

    removeZone: async (id) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        await ds.zones.delete(id);
        set((state) => ({
          zones: state.zones.filter((z) => z.id !== id),
          projects: state.projects.map((p) =>
            p.zoneId === id ? { ...p, zoneId: undefined } : p
          ),
          pipelines: state.pipelines.filter(
            (p) => !(p.fromType === "zone" && p.fromId === id) &&
                   !(p.toType === "zone" && p.toId === id)
          ),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        console.error("Failed to delete zone:", error);
        set({ saveError: (error as Error).message, isSaving: false });
        return false;
      }
    },

    addPipeline: async (pipeline) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        const mapped = await ds.pipelines.create({
          fromId: pipeline.fromId,
          toId: pipeline.toId,
          fromType: pipeline.fromType,
          toType: pipeline.toType,
          direction: pipeline.direction,
          animated: pipeline.animated,
        });
        set((state) => ({
          pipelines: [...state.pipelines, mapped],
          isSaving: false,
        }));
        return mapped;
      } catch (error) {
        console.error("Failed to add pipeline:", error);
        set({ saveError: (error as Error).message, isSaving: false });
        return null;
      }
    },

    updatePipeline: async (id, updates) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        await ds.pipelines.update(id, {
          fromId: updates.fromId,
          toId: updates.toId,
          fromType: updates.fromType,
          toType: updates.toType,
          direction: updates.direction,
          animated: updates.animated,
        });
        set((state) => ({
          pipelines: state.pipelines.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          ),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        console.error("Failed to update pipeline:", error);
        set({ saveError: (error as Error).message, isSaving: false });
        return false;
      }
    },

    removePipeline: async (id) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        await ds.pipelines.delete(id);
        set((state) => ({
          pipelines: state.pipelines.filter((p) => p.id !== id),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        console.error("Failed to delete pipeline:", error);
        set({ saveError: (error as Error).message, isSaving: false });
        return false;
      }
    },

    removeTask: async (taskId) => {
      set({ isSaving: true, saveError: null });
      try {
        const ds = await getActiveDataSource();
        await ds.tasks.delete(taskId);
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== taskId),
          isSaving: false,
        }));
        return true;
      } catch (error) {
        console.error("Failed to delete task:", error);
        set({ saveError: (error as Error).message, isSaving: false });
        return false;
      }
    },

    clearSaveError: () => {
      set({ saveError: null });
    },
  }))
);

// Re-fetch world data when active workspace changes
useWorkspaceStore.subscribe((state, prevState) => {
  if (state.activeWorkspaceId && state.activeWorkspaceId !== prevState.activeWorkspaceId) {
    // Reset fetch guard so a new fetch can start
    isFetchingWorld = false;
    // Abort any in-flight fetch
    if (worldFetchAbort) worldFetchAbort.abort();
    // Clear stale data immediately so UI reflects the switch
    useWorldStore.setState({
      projects: [],
      zones: [],
      agents: [],
      tasks: [],
      pipelines: [],
      isLoading: true,
      error: null,
    });
    // Fetch data for the new workspace
    useWorldStore.getState().fetchWorldData();
  }
});
